// CORE
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';

// APP-COMPONENTS
import { accountsTypes, accountsUtils } from 'app-components/flux/accounts';
import { actionHelpers } from 'app-components/flux/core';
import { rootUiActions } from 'app-components/flux/root-ui';
import { loadState, useLoadState } from 'app-components/hooks/utils';
import { getLogger, tracker } from 'app-components/utils/core';

// MUI
import ErrorIcon from '@mui/icons-material/Error';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

// DATA
import * as fundingAccountsActions from 'data/fundingAccounts/actions';
import * as fundingAccountSelectors from 'data/fundingAccounts/selectors';
import { statusEnum } from 'data/fundingAccounts/types';

import type { MicrodepositConfirmationDialogProps } from 'components/Dialogs/A2A/actions';
import { showAccountLockoutDialog, showInitiateTransferDialog } from 'components/Dialogs/A2A/actions';
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import StdDialogContent from 'components/Dialogs/StdDialogContent';
import Dump from 'components/Dump';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import InstitutionIcon from 'components/InstitutionIcon';
import LoadingView from 'components/LoadingView';
import QAmountField from 'components/QAmountField';
import QButton from 'components/QButton';
// COMPONENTS
import QIllustration from 'components/QIllustration';
import ZeroStateView from 'components/ZeroStateView';

const log = getLogger('components/Dialogs/A2A/MicroDepositConfirmationDialog');

const useStyles = makeStyles()((theme) => ({
  amountField: {
    textAlign: 'left',
    whiteSpace: 'pre-wrap',
    marginBottom: 20,
  },
  inputField: {
    textAlign: 'left',
  },
  fromAccount: {
    marginTop: 20,
    marginBottom: 25,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  amountBox: {
    display: 'flex',
    flexDirection: 'column',
  },
  successOrFailureTab: {
    padding: `${theme.spacing(8)} ${theme.spacing(15)}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  viewOneWrapper: {
    marginBottom: 10,
    overflowX: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'row',
    overflowY: 'auto',
  },
  institutionSelectWrapper: {
    paddingRight: 20,
    width: '100%',
    marginLeft: 10,
    display: 'flex',
    flexDirection: 'column',
  },
  instituteIconWrapper: {
    minWidth: 64,
  },
  accountEligibleTitle: {
    marginTop: 8,
  },
  accountEligibleNumber: {
    marginBottom: 10,
    color: theme.palette.text.secondary,
  },
  errorTextWrap: {
    backgroundColor: theme.palette.error.light,
    display: 'flex',
    flexDirection: 'row',
    borderRadius: 12,
    padding: '8px 16px 8px 16px',
  },
  errorIcon: {
    color: theme.palette.error.main,
    marginRight: 5,
  },
  infoIcon: {
    marginBottom: 5,
    marginLeft: 5,
  },
}));

const indexEnums = {
  FAILURE_DEPOSIT: 'FAILURE_DEPOSIT',
  FAILURE_VERIFICATION: 'FAILURE_VERIFICATION',
  LOADING: 'LOADING',
  SUBMIT: 'SUBMIT',
  SUCCESS: 'SUCCESS',
};

const MicrodepositConfirmationDialog = React.memo((props: MicrodepositConfirmationDialogProps) => {
  const isMounted = useRef<boolean>(undefined);
  const { dialogId, onClose, initiator, fundingAccountId } = props;
  const dispatch = useAppDispatch();
  const { classes } = useStyles();

  // ==================== Grab Funding Account By ID! =============================
  const fundingAccount = useAppSelector((state) =>
    fundingAccountSelectors.getFundingAccountById(state, fundingAccountId),
  );

  // =================== STATES ==================
  const [depositValueOne, setDepositValueOne] = useState(0);
  const [depositValueTwo, setDepositValueTwo] = useState(0);
  const [hideDialog, setHideDialog] = useState(false);
  const [errorCode, setErrorCode] = useState('');
  const [tabIndex, setTabIndex] = useState(indexEnums.SUBMIT);

  // ================ SELECTORS ==================
  const account = useAppSelector((state) =>
    fundingAccountSelectors.getAccountByFundingAccountId(state, fundingAccount?.id),
  );

  //  ================= VIEW STATE LOGIC =====================
  const view = useLoadState(fundingAccountSelectors);

  const mainView = useMemo(() => {
    const showSkeletonView = loadState.skeletonStates.includes(view) || tabIndex === indexEnums.LOADING;
    const showTryAgainView =
      (loadState.tryAgainStates.includes(view) || loadState.updateRequiredStates.includes(view)) &&
      fundingAccount &&
      tabIndex !== indexEnums.LOADING;
    const showNoDataView =
      (loadState.noDataStates.includes(view) || !fundingAccount) && tabIndex !== indexEnums.LOADING;

    const showReadyView = loadState.readyStates.includes(view) && fundingAccount && tabIndex !== indexEnums.LOADING;

    let myMainView = '';
    if (showReadyView) {
      myMainView = 'READY';
    } else if (showNoDataView) {
      myMainView = 'EMPTY';
    } else if (showTryAgainView) {
      myMainView = 'FAILED';
    } else if (showSkeletonView) {
      myMainView = 'LOADING';
    }

    //  OVERRIDE mainView for unique tabIndex exceptions
    if (
      (tabIndex === indexEnums.SUCCESS ||
        tabIndex === indexEnums.FAILURE_DEPOSIT ||
        tabIndex === indexEnums.FAILURE_VERIFICATION) &&
      myMainView === 'EMPTY'
    ) {
      myMainView = 'READY';
    }
    return myMainView;
  }, [fundingAccount, tabIndex, view]);

  // ========== EFFECTS =============

  // track mounted state because of async promise
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    tracker.track(tracker.events.viewMicrodeposits, { initiator });
  }, [initiator]);

  // ========== CALLBACKS =============
  const handleClose = () => {
    dispatch(rootUiActions.removeDialog(dialogId));
    onClose?.();
  };

  const getFundingAccounts = useCallback(() => {
    dispatch(fundingAccountsActions.getFundingAccounts(undefined));
  }, [dispatch]);

  /* Formats the final 4 digits of the account number */
  const getFinalFourFormatted = useCallback((account?: accountsTypes.Account, includeName?: boolean) => {
    if (!account) {
      log.warn('Account missing in final4 lookup');
      return '????';
    }
    if (includeName) {
      return `${account?.name} ${accountsUtils.formatFinalFour(account)}`;
    }
    const accountSubtitle = `${
      accountsTypes.getAccountTypeNodeByQcsType(account.type, account.subType).displayName
    } ${accountsUtils.formatFinalFour(account)}`;
    return accountSubtitle;
  }, []);

  const openLockoutDialog = (_e?: any) => {
    setHideDialog(true);
    dispatch(
      showAccountLockoutDialog({
        onClose: handleClose,
      }),
    );
  };

  const kickoffTransferFlow = (_e?: any) => {
    setHideDialog(true);
    dispatch(
      showInitiateTransferDialog({
        onClose: handleClose,
        initiator: 'micro deposit success screen',
      }),
    );
  };

  const handleContinue = () => {
    if (tabIndex === indexEnums.SUBMIT) {
      setTabIndex(indexEnums.LOADING);

      if (fundingAccount?.verificationId) {
        const payload = {
          id: fundingAccount.verificationId,
          amountOne: String(depositValueOne),
          amountTwo: String(depositValueTwo),
        };
        actionHelpers
          .bindPromiseAction(
            dispatch,
            fundingAccountsActions.verifyMicroDepositsAction,
          )(payload)
          .then((resp: any) => {
            if (resp?.data?.status === statusEnum.VERIFIED) {
              tracker.track(tracker.events.microdepositSubmit, { status: 'success' });
              if (isMounted.current === true) {
                setTabIndex(indexEnums.SUCCESS);
              }
            } else if (resp?.data?.attempts > resp?.data.threshold) {
              // If it comes back as a non-error, but failed
              tracker.track(tracker.events.microdepositSubmit, { status: 'locked' });
              if (isMounted.current === true) {
                setTabIndex(indexEnums.FAILURE_VERIFICATION);
              }
            } else if (
              resp?.data?.status === statusEnum.FAILED ||
              resp?.data?.status === statusEnum.UNVERIFIED ||
              resp?.data?.status === statusEnum.DISABLED
            ) {
              // If it comes back as a non-error, but failed
              tracker.track(tracker.events.microdepositSubmit, { status: 'locked' });
              openLockoutDialog();
            } else {
              tracker.track(tracker.events.microdepositSubmit, { status: 'failure' });
              if (isMounted.current === true) {
                setTabIndex(indexEnums.SUBMIT);
                setErrorCode('Microdeposits Invalid');
              }
            }
          })
          .catch((_error: any) => {
            tracker.track(tracker.events.microdepositSubmit, { status: 'failure' });
            if (isMounted.current === true) {
              setTabIndex(indexEnums.FAILURE_DEPOSIT);
            }
          });
      }
    }
    if (tabIndex === indexEnums.FAILURE_DEPOSIT || tabIndex === indexEnums.FAILURE_VERIFICATION) {
      handleClose();
    }
    if (tabIndex === indexEnums.SUCCESS) {
      kickoffTransferFlow();
    }
  };

  // =============== DERIVED VALUES ===================
  let dialogTitle = 'Verify deposits for account transfers';
  if (tabIndex === indexEnums.SUCCESS) dialogTitle = 'Account verified';
  if (tabIndex === indexEnums.FAILURE_VERIFICATION) dialogTitle = 'Account not verified';
  if (tabIndex === indexEnums.FAILURE_DEPOSIT) dialogTitle = 'Account verification failed';

  let disableContinue = false;
  // verify that we have at least 2 valid accounts and no fieldErrors
  if (
    (tabIndex === indexEnums.SUBMIT &&
      (depositValueOne >= 1 ||
        depositValueOne <= 0 ||
        depositValueTwo >= 1 ||
        depositValueTwo <= 0 ||
        !fundingAccount)) ||
    tabIndex === indexEnums.LOADING
  ) {
    disableContinue = true;
  }

  let primaryVariant: 'outlined' | 'contained' | undefined = 'outlined';
  if (tabIndex === indexEnums.SUBMIT || tabIndex === indexEnums.SUCCESS) primaryVariant = 'contained';
  if (tabIndex === indexEnums.LOADING) primaryVariant = undefined;

  let continueText = 'continue';
  if (tabIndex === indexEnums.SUCCESS) continueText = 'transfer funds';
  if (tabIndex === indexEnums.FAILURE_DEPOSIT || tabIndex === indexEnums.FAILURE_VERIFICATION) continueText = 'close';
  if (tabIndex === indexEnums.LOADING) continueText = '';

  // ================= RENDER ================
  return (
    <StdDialog
      dialogId='this_dialog_id_exists'
      disableBackdropClick={tabIndex === indexEnums.LOADING}
      //  onBack={(_) => decTabIndex()}
      onClose={handleClose}
      open={!hideDialog}
      sharedcomponentid='DIALOG_TYPE_MICRODEPOSIT_CONFIRMATION_FOR_TRANSFERS'
      showCloseButton={tabIndex !== indexEnums.LOADING}
      size='md'
      title={dialogTitle}
    >
      <StdDialogContent dialogId='this_dialog_id_exists'>
        {tabIndex === indexEnums.SUBMIT && mainView === 'READY' ? (
          <>
            <Dump value={fundingAccount} />
            <div className={classes.viewOneWrapper}>
              <div className={classes.instituteIconWrapper}>
                <InstitutionIcon accountId={account?.id ?? ''} />
              </div>
              <div className={classes.institutionSelectWrapper}>
                <Typography variant='titleMedium' className={classes.accountEligibleTitle}>
                  {account?.name}
                </Typography>
                <Typography variant='paragraphRegular' className={classes.accountEligibleNumber}>
                  {getFinalFourFormatted(account, false)}
                </Typography>

                {!errorCode ? (
                  <Typography variant='bodyRegular'>
                    {'Enter the two small deposit amounts sent to this account.'}
                    <InfoTooltipIcon
                      className={classes.infoIcon}
                      tooltipPlacement='right'
                      tooltipHTML={
                        <span>
                          <span>
                            {
                              'Look for two small deposits (less than $1 each) that we sent to this account to verify it for transfers.'
                            }
                          </span>
                          <br />
                          <br />
                          <span>
                            {
                              'If you don’t see these deposits, try refreshing your accounts in Simplifi or log into your bank’s website.'
                            }
                          </span>
                        </span>
                      }
                    />
                  </Typography>
                ) : null}
                {errorCode ? (
                  <div className={classes.errorTextWrap}>
                    <ErrorIcon className={classes.errorIcon} />
                    <Typography>{'Incorrect amounts entered. Please check your entries and try again.'}</Typography>
                  </div>
                ) : null}
                <Box className={classes.fromAccount} display='flex' alignItems='center' my={1}>
                  <div className={classes.amountBox}>
                    <QAmountField
                      noNegatives
                      expectedSign='positive'
                      value={Number(depositValueOne)}
                      onChange={(event) => {
                        setDepositValueOne(event.target.value);
                      }}
                      textFieldVariant={'filled'}
                      editable
                      id='deposit-value-field-one'
                      label='Deposit amount 1'
                      variant='filled'
                      autoComplete='one-time-code'
                      className={classes.amountField}
                      classes={{ inputField: classes.inputField }}
                      error={Number(depositValueOne) >= 1}
                      helperText={Number(depositValueOne) >= 1 && 'Amount must be under $1.00'}
                    />
                    <QAmountField
                      noNegatives
                      expectedSign='positive'
                      value={Number(depositValueTwo)}
                      onChange={(event) => {
                        setDepositValueTwo(event.target.value);
                      }}
                      textFieldVariant={'filled'}
                      editable
                      id='deposit-value-field-two'
                      label='Deposit amount 2'
                      variant='filled'
                      autoComplete='one-time-code'
                      className={classes.amountField}
                      classes={{ inputField: classes.inputField }}
                      error={Number(depositValueTwo) >= 1}
                      helperText={Number(depositValueTwo) >= 1 && 'Amount must be under $1.00'}
                    />
                  </div>
                </Box>
              </div>
            </div>
          </>
        ) : null}
        {tabIndex === indexEnums.SUCCESS && mainView === 'READY' && (
          <Box className={classes.successOrFailureTab}>
            <QIllustration name='thumbsUp' size='lg' />
            <Box mt={3} mb={3}>
              <Typography variant='displayRegular'>Account ready for transfers!</Typography>
            </Box>
            <Typography variant='bodyRegular' color='textSecondary'>
              Transfer money now, or use the <b>Transfer Money</b> button at the top of this account on the Transactions
              page.
            </Typography>
          </Box>
        )}
        {tabIndex === indexEnums.FAILURE_VERIFICATION && mainView === 'READY' && (
          <Box className={classes.successOrFailureTab}>
            <ZeroStateView
              id='microdeposit-failure'
              title='Account could not be verified'
              description={
                'We’re sorry, but you’ve exceeded the maximum number of verification attempts. To verify this account for transfers, please go to the Help Center, and initiate a chat with one of our support agents.'
              }
              qIllustration='starSad'
            />
          </Box>
        )}
        {tabIndex === indexEnums.FAILURE_DEPOSIT && mainView === 'READY' && (
          <ZeroStateView
            id='microdeposit-failure'
            title='Account verification failed'
            description={
              'We’re sorry, but verification deposits could not be delivered to this account. Please go to the Help Center, and initiate a chat with one of our support agents.'
            }
            qIllustration='planeCrash'
          />
        )}
        {mainView === 'LOADING' && (
          <Box
            paddingTop={20}
            paddingBottom={20}
            display='flex'
            alignItems='center'
            flexDirection='column'
            justifyContent='center'
          >
            <LoadingView size={100} />
          </Box>
        )}
        {mainView === 'FAILED' && (
          <ZeroStateView
            className={classes.successOrFailureTab}
            button={
              <QButton id={'try-again-button'} variant='outlined' onClick={getFundingAccounts}>
                TRY AGAIN
              </QButton>
            }
            id='failed-to-fetch'
            title='Unable to fetch data'
            description="We can't fetch your data from the server. Please try again"
            qIllustration='planeCrash'
          />
        )}
        {mainView === 'EMPTY' && tabIndex !== indexEnums.FAILURE_VERIFICATION && (
          <ZeroStateView
            className={classes.successOrFailureTab}
            id='zero-state'
            title='No data available'
            description='Your account may be in error. Please try again'
            qIllustration='emptyBox'
          />
        )}
      </StdDialogContent>
      {mainView === 'READY' && tabIndex === indexEnums.SUCCESS && (
        <StdDialogActions
          primary={{
            disabled: disableContinue,
            children: continueText,
            onClick: handleContinue,
            variant: primaryVariant,
          }}
          secondary={{
            children: 'close',
            onClick: handleClose,
            variant: 'outlined',
          }}
        />
      )}
      {mainView === 'READY' && tabIndex !== indexEnums.SUCCESS && (
        <StdDialogActions
          primary={{
            disabled: disableContinue,
            children: continueText,
            onClick: handleContinue,
            variant: primaryVariant,
          }}
        />
      )}
    </StdDialog>
  );
});

MicrodepositConfirmationDialog.displayName = 'MicrodepositConfirmationDialog';

export default MicrodepositConfirmationDialog;
